const API_BASE = "/api/v1"

export interface StoryEntry {
  id: number
  entry_type: "milestone" | "observation" | "photo" | "note" | "conversation" | "perspective" | "idea"
  title: string
  body: string | null
  occurred_at: string
  automatic: boolean
  user_note: string | null
  created_at: string
  updated_at?: string
  image_url?: string | null
  source_type?: string | null
  source_id?: number | null
  linked_dog_names?: string[]
  editable?: boolean
  deletable?: boolean
}

export interface StoryIndexResponse {
  entries: StoryEntry[]
  has_entitlement: boolean
  read_only?: boolean
  dog_id?: number
  dog_name?: string
  message?: string
}

async function parseJsonResponse<T>(res: Response): Promise<T> {
  const text = await res.text()
  if (!text.trim()) {
    throw new Error(`Empty response from server (${res.status})`)
  }
  try {
    return JSON.parse(text) as T
  } catch {
    throw new Error(`Unexpected response from server (${res.status})`)
  }
}

async function apiErrorMessage(res: Response, bodyText?: string): Promise<string> {
  const text = bodyText ?? (await res.text())
  try {
    const err = JSON.parse(text) as { errors?: string[] }
    if (err?.errors?.length) return err.errors.join(", ")
  } catch {
    // non-json error body
  }
  return res.statusText || `Request failed (${res.status})`
}

export async function fetchStory(dogId?: number): Promise<StoryIndexResponse> {
  const url = dogId ? `${API_BASE}/story?dog_id=${dogId}` : `${API_BASE}/story`
  const res = await fetch(url, { credentials: "include", headers: { Accept: "application/json" } })
  if (!res.ok) throw new Error(await apiErrorMessage(res))
  return parseJsonResponse<StoryIndexResponse>(res)
}

export async function createStoryEntry(
  dogId: number | undefined,
  entry: {
    entry_type: string
    title: string
    body?: string
    occurred_at: string
    user_note?: string
  },
  image?: File | null,
): Promise<{ entry: StoryEntry }> {
  const url = dogId ? `${API_BASE}/story?dog_id=${dogId}` : `${API_BASE}/story`

  if (image) {
    const form = new FormData()
    form.append("entry[entry_type]", entry.entry_type)
    form.append("entry[title]", entry.title)
    if (entry.body) form.append("entry[body]", entry.body)
    form.append("entry[occurred_at]", entry.occurred_at)
    if (entry.user_note) form.append("entry[user_note]", entry.user_note)
    form.append("image", image)
    const res = await fetch(url, {
      method: "POST",
      body: form,
      credentials: "include",
      headers: { Accept: "application/json" },
    })
    const bodyText = await res.text()
    if (!res.ok) throw new Error(await apiErrorMessage(res, bodyText))
    return parseJsonResponse<{ entry: StoryEntry }>(new Response(bodyText, { headers: res.headers }))
  }

  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ entry }),
    credentials: "include",
  })
  if (!res.ok) throw new Error(await apiErrorMessage(res))
  return res.json()
}

export async function saveAskConversationToStory(payload: {
  messageId: number
  dogIds: number[]
  userNote?: string
}): Promise<{
  entries: StoryEntry[]
  already_saved: boolean
  created_dog_ids: number[]
  existing_dog_ids: number[]
}> {
  const res = await fetch(`${API_BASE}/story/from_ask`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      message_id: payload.messageId,
      dog_ids: payload.dogIds,
      user_note: payload.userNote,
    }),
    credentials: "include",
  })
  if (!res.ok) throw new Error(await apiErrorMessage(res))
  return res.json()
}

export async function fetchAskStorySavedStatus(
  messageId: number,
  dogIds: number[],
): Promise<{ saved_dog_ids: number[]; all_selected_saved: boolean }> {
  const params = new URLSearchParams()
  params.set("message_id", String(messageId))
  dogIds.forEach((id) => params.append("dog_ids[]", String(id)))
  const res = await fetch(`${API_BASE}/story/saved_status?${params}`, {
    credentials: "include",
  })
  if (!res.ok) throw new Error(await apiErrorMessage(res))
  return res.json()
}

export async function updateStoryEntry(
  entryId: number,
  updates: {
    title?: string
    body?: string
    occurred_at?: string
    user_note?: string
  },
  dogId?: number,
): Promise<{ entry: StoryEntry }> {
  const url = dogId
    ? `${API_BASE}/story/${entryId}?dog_id=${dogId}`
    : `${API_BASE}/story/${entryId}`
  const res = await fetch(url, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ entry: updates }),
    credentials: "include",
  })
  if (!res.ok) throw new Error(await apiErrorMessage(res))
  return res.json()
}

export async function deleteStoryEntry(entryId: number, dogId?: number): Promise<{ removed_from?: string[] }> {
  const url = dogId
    ? `${API_BASE}/story/${entryId}?dog_id=${dogId}`
    : `${API_BASE}/story/${entryId}`
  const res = await fetch(url, {
    method: "DELETE",
    credentials: "include",
  })
  if (!res.ok) throw new Error(await apiErrorMessage(res))
  return res.json().catch(() => ({}))
}
